import Vue from "vue";
import store from "@/store";
import router from "@/router";
import axios from "axios";
import i18n from "@/libs/i18n";
import Echo from "laravel-echo";
import usePassport from "@/auth/passport/usePassport";
import { ref } from "@vue/composition-api";
import ability from "@/libs/acl/ability";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { getHomeRouteForLoggedInUser } from "@/auth/utils";
import { encryptData } from "@/utils/encryptUserDataUtils";

export default function useSingleSignOn() {
  const getPlatformUrl = async (platform) => {
    window.location.href = `${window.__env.VUE_APP_BASE_URL}/v1/auth/platform/redirect?platform=${platform}`;
  };

  const handleSocialAuthCallback = async (route) => {
    try {
      if (!validateLogin(route)) return;
      const access_token = route.access_token;
      store.commit("locale_login/updateLoginLoading", true);
      const getUserData = await axios.get(
        `${window.__env.VUE_APP_BASE_URL}/v1/auth/getUserInfo`,
        {
          params: {
            user_id: route.user_id,
            expires_at: route.expires_at,
          },
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        }
      );
      const userData = getUserData.data.data.user;
      setUserInfo(access_token, getUserData);
      setWebsocket(access_token);
      sendSuccessNotification(userData);
      store.commit("locale_login/updateLoginLoading", false);
      router.replace(getHomeRouteForLoggedInUser(userData));
    } catch {
      store.commit("locale_login/updateLoginLoading", false);
      validateLogin({ success: "0" });
    }
  };

  const sendSuccessNotification = (userData) => {
    Vue.$toast({
      component: ToastificationContent,
      position: "top-right",
      props: {
        title: `Hello ${userData.name}`,
        icon: "CoffeeIcon",
        variant: "success",
        text: "Welcome back",
      },
    });
  };

  const setWebsocket = (access_token) => {
    const echoConfig = {
      broadcaster: "pusher",
      key: window.__env?.VUE_APP_LARAVEL_PUSHER_APP_KEY,
      wsHost: window.__env?.VUE_APP_LARAVEL_PUSHER_HOST,
      wsPort: window.__env?.VUE_APP_LARAVEL_PUSHER_PORT,
      wssPort: window.__env?.VUE_APP_LARAVEL_PUSHER_PORT,
      cluster: window.__env?.VUE_APP_LARAVEL_PUSHER_APP_CLUSTER || "mt1",
      forceTLS: window.__env?.VUE_APP_LARAVEL_PUSHER_ENABLED_TLS || true,
      encrypted: window.__env?.VUE_APP_LARAVEL_PUSHER_ENABLED_TLS || true,
      disableStats: true,
      enabledTransports: ["ws"],
      authEndpoint: `${window.__env?.VUE_APP_BASE_URL}/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    };

    // Solo agregar wsPath si tiene un valor
    if (window.__env?.VUE_APP_LARAVEL_PUSHER_PATH) {
      echoConfig.wsPath = window.__env.VUE_APP_LARAVEL_PUSHER_PATH;
    }

    window.Echo = new Echo(echoConfig);
  };

  const validateLogin = (route) => {
    if (Object.keys(route).length === 0) return;
    if (route.success === "0") {
      let notification = route.message ? route.message : "Error on login";
      Vue.$toast({
        component: ToastificationContent,
        position: "top-right",
        props: {
          title: notification,
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
      router.push("/");
      return false;
    }
    if (!route.access_token) return false;
    return true;
  };

  const setUserInfo = async (token, info) => {
    const access_token = token;
    const userData = info.data.data.user;
    const additionalTokenImage = info.data.data.additionalTokenImage;

    usePassport.setToken(access_token);
    userData.ability = [];
    i18n.locale = userData.lang;
    store.commit("locale_login/setData", userData.lang);
    if (userData.video_wall) {
      store
        .dispatch("video_wall/ping", userData)
        .then((response) => {
          console.log(response);
        })
        .catch((response) => console.log(response));
    }
    if (userData.is_master_admin) {
      userData.ability.push({
        action: "manage",
        subject: "all",
      });
    } else {
      userData.ability = userData.abilities;
      userData.ability.push({
        action: "read",
        subject: "Auth",
      });

      userData.ability.push({
        action: "read",
        subject: "Home",
      });
    }

    if (userData.profile.token_lifetime) {
      let date = new Date();
      userData.profile.date_expirationToken = date.setHours(
        date.getHours() + userData.profile.token_lifetime
      );
    }

    ability.update(userData.ability);
    const userDataEncrypted = encryptData(userData);
    localStorage.setItem("userData", userDataEncrypted);
    localStorage.setItem("additionalTokenImage", additionalTokenImage);
    store.dispatch("user/setUserDataFromLocalStorage", userData);
  };

  return { getPlatformUrl, handleSocialAuthCallback };
}
